import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import { useDashboardConfig } from '../../hooks/useDashboardConfig';
import { WIDGET_MAP } from './dashboardWidgetRegistry';
import DashboardCustomizeModal from './DashboardCustomizeModal';
import DashboardStats from './DashboardStats';
import QuickActionsBar from './QuickActionsBar';
import AIPrioritySuggestions from './AIPrioritySuggestions';
import DashboardFeed from './DashboardFeed';
import PipelineOverview from './PipelineOverview';
import PerformanceTrends from './PerformanceTrends';
import UpcomingCalendar from './UpcomingCalendar';
import SalesRadio from './SalesRadio';
import MorningBrief from './MorningBrief';
import TodaysFocus from './TodaysFocus';
import Top3Priorities from './Top3Priorities';
import RecentWins from './RecentWins';
import OrganizationMembersWidget from './OrganizationMembersWidget';
import EchobotWrapper from '../Echobot/EchobotWrapper';
import { IconButton } from '../shared';
import type { Lead, Task, Opportunity } from '../../types';
import type { AuthUser } from '../../hooks/useAuth';

interface DashboardRendererProps {
  user: AuthUser;
  leads: Lead[];
  tasks: Task[];
  opportunities: Opportunity[];
  dashLeads: Lead[];
  dashTasks: Task[];
  dashOpps: Opportunity[];
  dashboardView: 'user' | 'team';
  onSaveDashboardView: (v: 'user' | 'team') => void;
  onSetCurrentView: (view: string) => void;
  onAddLead: () => void;
  onAddTask: () => void;
  onAddOpportunity: () => void;
  onCreateOffer: () => void;
  onOpenAgentMode?: () => void;
  onOpenMCPAdmin?: () => void;
  onSelectContact: (lead: Lead | null) => void;
  onSelectOpportunity: (opp: Opportunity | null) => void;
  onSelectTask: (task: Task | null) => void;
}

const DashboardRenderer: React.FC<DashboardRendererProps> = ({
  user,
  leads,
  tasks,
  opportunities,
  dashLeads,
  dashTasks,
  dashOpps,
  dashboardView,
  onSaveDashboardView,
  onSetCurrentView,
  onAddLead,
  onAddTask,
  onAddOpportunity,
  onCreateOffer,
  onOpenAgentMode,
  onOpenMCPAdmin,
  onSelectContact,
  onSelectOpportunity,
  onSelectTask,
}) => {
  const { t } = useTranslation('dashboard');
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const dashboardOrgId = user?.profile?.orgId || user?.uid;

  const {
    widgetIds,
    availableIds,
    updateConfig,
    resetToDefault,
    isPinned,
  } = useDashboardConfig(user?.uid);

  const navigateToRecommendationTarget = (view: string, id?: string) => {
    onSelectContact(null);
    onSelectOpportunity(null);
    onSelectTask(null);

    if (id) {
      if (view === 'leads') {
        onSelectContact(leads.find(l => l.id === id) || null);
      } else if (view === 'opportunities') {
        onSelectOpportunity(opportunities.find(o => o.id === id) || null);
      } else if (view === 'tasks') {
        onSelectTask(tasks.find(t => t.id === id) || null);
      }
    }

    if (view === 'dashboard') {
      onSaveDashboardView('team');
    }

    onSetCurrentView(view);
  };

  const renderWidget = (id: string) => {
    switch (id) {
      case 'quick-actions':
        return (
          <QuickActionsBar
            currentUser={user?.profile}
            orgId={dashboardOrgId}
            onAddLead={onAddLead}
            onAddTask={onAddTask}
            onAddOpportunity={onAddOpportunity}
            onCreateOffer={onCreateOffer}
            onNavigate={onSetCurrentView}
            onOpenAgentMode={onOpenAgentMode}
            onOpenMCPAdmin={onOpenMCPAdmin}
          />
        );
      case 'stats':
        return (
          <DashboardStats
            leads={dashLeads}
            tasks={dashTasks}
            opportunities={dashOpps}
          />
        );
      case 'ai-suggestions':
        return (
          <div className="h-[700px]">
            <AIPrioritySuggestions
              leads={dashLeads}
              tasks={dashTasks}
              opportunities={dashOpps}
              onNavigate={navigateToRecommendationTarget}
            />
          </div>
        );
      case 'activity-feed':
        return (
          <div className="h-[700px]">
            <DashboardFeed
              orgId={dashboardOrgId}
              currentUserId={user?.uid}
              currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
              currentUserPhoto={user?.photoURL || user?.profile?.avatar}
              maxPosts={10}
              onViewAll={() => onSetCurrentView('stream')}
            />
          </div>
        );
      case 'pipeline':
        return (
          <PipelineOverview
            opportunities={dashOpps}
            onNavigate={onSetCurrentView}
          />
        );
      case 'trends':
        return (
          <PerformanceTrends
            leads={dashLeads}
            tasks={dashTasks}
            opportunities={dashOpps}
          />
        );
      case 'calendar':
        return (
          <UpcomingCalendar
            tasks={dashTasks}
            opportunities={dashOpps}
            onNavigateToTask={(taskId) => navigateToRecommendationTarget('tasks', taskId)}
            onNavigateToOpportunity={(oppId) => navigateToRecommendationTarget('opportunities', oppId)}
          />
        );
      case 'sales-radio':
        return (
          <SalesRadio
            orgId={dashboardOrgId}
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            isAdmin={user?.profile?.role === 'admin'}
          />
        );
      case 'morning-brief':
        return (
          <MorningBrief
            leads={dashLeads}
            tasks={dashTasks}
            opportunities={dashOpps}
          />
        );
      case 'todays-focus':
        return (
          <TodaysFocus
            leads={dashLeads}
            tasks={dashTasks}
            opportunities={dashOpps}
            onNavigateToTask={(taskId) => navigateToRecommendationTarget('tasks', taskId)}
            onNavigateToLead={(leadId) => navigateToRecommendationTarget('leads', leadId)}
            onNavigateToOpportunity={(oppId) => navigateToRecommendationTarget('opportunities', oppId)}
          />
        );
      case 'top-priorities':
        return (
          <Top3Priorities
            leads={dashLeads}
            tasks={dashTasks}
            opportunities={dashOpps}
            onNavigate={(type, id) => {
              if (type === 'lead') {
                navigateToRecommendationTarget('leads', id);
              } else if (type === 'opportunity') {
                navigateToRecommendationTarget('opportunities', id);
              } else if (type === 'task') {
                navigateToRecommendationTarget('tasks', id);
              }
            }}
          />
        );
      case 'recent-wins':
        return (
          <RecentWins opportunities={dashOpps} />
        );
      case 'echobot':
        return (
          <EchobotWrapper
            context="dashboard"
            userRole={user?.profile?.role || 'user'}
            organizationStats={{
              leadCount: dashLeads.length,
              opportunityCount: dashOpps.length,
              campaignCount: 0,
              taskCount: dashTasks.filter(t => !t.completed).length,
              overdueTasks: dashTasks.filter(t => !t.completed && new Date(t.dueDate) < new Date()).length,
              inactiveOpportunities: dashOpps.filter(o => {
                const lastActivity = o.summary?.lastActivity || o.updatedAt;
                const daysSince = (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24);
                return daysSince > 3 && o.stage !== 'closed_won' && o.stage !== 'closed_lost';
              }).length,
              unassignedLeads: dashLeads.filter(l => !l.assignedTo).length,
            }}
            userData={{
              tasks: dashTasks.map(t => ({
                id: t.id,
                title: t.title,
                description: t.description,
                dueDate: t.dueDate,
                completed: t.completed,
                priority: t.priority,
                type: t.type,
                leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName,
              })),
              opportunities: dashOpps.map(o => ({
                id: o.id,
                title: o.title,
                stage: o.stage,
                value: o.value,
                companyName: o.company?.name,
                probability: o.probability,
                expectedCloseDate: o.expectedCloseDate,
              })),
              leads: dashLeads.map(l => ({
                id: l.id,
                firstName: l.firstName,
                lastName: l.lastName,
                company: l.company,
                stage: l.stage,
                score: l.score,
              })),
            }}
            onActionClick={(action) => {
              if (action.startsWith('navigate-to-')) {
                onSetCurrentView(action.replace('navigate-to-', ''));
              } else if (action === 'open-add-lead-modal') {
                onAddLead();
              } else if (action === 'open-add-task-modal') {
                onAddTask();
              } else if (action === 'open-add-opportunity-modal') {
                onAddOpportunity();
              }
            }}
            showOnMount={true}
            enableProactive={true}
          />
        );
      default:
        return null;
    }
  };

  const widgetElements: React.ReactNode[] = [];
  const resolvedWidgets = widgetIds
    .map(id => ({ id, def: WIDGET_MAP.get(id) }))
    .filter((w): w is { id: string; def: NonNullable<typeof w.def> } => !!w.def);

  let i = 0;
  while (i < resolvedWidgets.length) {
    const { id, def } = resolvedWidgets[i];

    if (def.size === 'full') {
      widgetElements.push(
        <div key={id}>{renderWidget(id)}</div>
      );
      i++;
    } else {
      const next = i + 1 < resolvedWidgets.length ? resolvedWidgets[i + 1] : null;
      if (next && next.def.size === 'half') {
        widgetElements.push(
          <div key={`${id}-${next.id}`} className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {renderWidget(id)}
            {renderWidget(next.id)}
          </div>
        );
        i += 2;
      } else {
        widgetElements.push(
          <div key={id}>{renderWidget(id)}</div>
        );
        i++;
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* View Toggle + Customize Button */}
      <div className="sticky top-0 z-30 -mx-1 bg-bg-base/95 px-1 py-2 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <OrganizationMembersWidget
            orgId={dashboardOrgId}
            currentUserId={user?.uid}
          />
          <div className="flex items-center justify-end gap-2">
            <div className="flex items-center gap-1 bg-bg-card rounded-pill p-1 border border-subtle">
              <button
                onClick={() => onSaveDashboardView('user')}
                className={`px-3 py-1.5 rounded-pill text-xs font-medium transition-all ${dashboardView === 'user' ? 'bg-accent text-txt-inverted' : 'text-txt-secondary hover:text-txt'}`}
              >
                {t('myKPIs')}
              </button>
              <button
                onClick={() => onSaveDashboardView('team')}
                className={`px-3 py-1.5 rounded-pill text-xs font-medium transition-all ${dashboardView === 'team' ? 'bg-accent text-txt-inverted' : 'text-txt-secondary hover:text-txt'}`}
              >
                {t('teamView')}
              </button>
            </div>
            <IconButton
              icon={<LayoutGrid className="w-4 h-4" strokeWidth={1.5} />}
              onClick={() => setCustomizeOpen(true)}
              title={t('customize.title')}
              size="sm"
              variant="ghost"
              className="text-txt-secondary hover:text-txt"
            />
          </div>
        </div>
      </div>

      {widgetElements}

      <DashboardCustomizeModal
        isOpen={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        visibleIds={widgetIds}
        availableIds={availableIds}
        isPinned={isPinned}
        onSave={updateConfig}
        onReset={resetToDefault}
      />
    </div>
  );
};

export default DashboardRenderer;
